import { useEffect, useRef, useState } from 'react';
import { googleSignupConfig } from '@biasafe.queries/auth-google-signup-config/auth-google-signup-config';
import { googleSignup } from '@biasafe.queries/auth-google-signup/auth-google-signup';
import { googleSignupNonce } from '@biasafe.queries/auth-google-signup-nonce/auth-google-signup-nonce';
import { loadGoogleIdentity } from '../gcp/googleIdentity';
import type { GisCredentialResponse } from '../gcp/googleIdentity';
import {
  firebaseAuthMessage,
  firebaseErrorCode,
} from '@/features/Auth/firebaseAuthMessage';
import { extractErrorMessage } from '@/shared/utils/apiErrors';

/**
 * Whether "Sign up with Google" should use the server-created-account flow.
 *
 * Asked of the backend, not of a build-time variable, so the button and the
 * routes behind it share ONE switch (`GOOGLE_SIGNUP_ENABLED`) and the UI can
 * never offer a flow the server refuses. Any failure reads as off: the page
 * then shows the button it always did.
 */
export function useGoogleSignupConfig(): {
  enabled: boolean;
  clientId: string | null;
} {
  const [clientId, setClientId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    googleSignupConfig()
      .then((cfg) => {
        if (!cancelled && cfg.enabled === true && cfg.client_id) {
          setClientId(cfg.client_id);
        }
      })
      // Off. The page shows the button it always did.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return { enabled: clientId !== null, clientId };
}

const NEED_TERMS =
  'Please accept the Terms of Service and Privacy Policy to create your account with Google.';
const SIGNS_IN_ANOTHER_WAY =
  'This email already signs in another way. Use the method you signed up with, such as your email and password.';

/**
 * Google's own "Sign up with Google" button, for a brand-new address.
 *
 * Identity Platform refuses client-side user creation, so `signInWithPopup`
 * cannot sign a new address up. Instead Google Identity Services hands the page
 * a signed Google ID token; the backend verifies it and creates the account;
 * and the same token then signs the user in. See
 * `qpm_api/v1/auth/google_signup.py` for the whole design.
 *
 * Google's button is rendered by Google (its policy and its iframe), so it
 * replaces ours rather than being restyled. Terms acceptance is the page's
 * existing checkbox, read through `acceptedTerms`; the server refuses a request
 * that does not say it was ticked.
 */
export function GoogleSignupButton({
  clientId,
  acceptedTerms,
  resumeWithSecondFactor,
  completeSignIn,
}: {
  clientId: string;
  acceptedTerms: boolean;
  /**
   * Called with any sign-in error before it is shown. Returns true when the
   * error was "a second factor is required" and the second-factor screen has
   * taken over (an existing, enrolled account is signing in, not failing to
   * sign up). Comes from `useAuthActions`, so every SSO path shares one rule.
   */
  resumeWithSecondFactor: (err: unknown) => Promise<boolean>;
  /**
   * Continue a signed-in session from the Firebase ID token: the normal login
   * continuation (session cookie, portal routing, profile, navigation).
   */
  completeSignIn: (firebaseIdToken: string) => Promise<void>;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const termsRef = useRef(acceptedTerms);
  termsRef.current = acceptedTerms;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Read through refs: the effect below must not re-initialise Google (and
  // spend another nonce) every time the page re-renders with new closures.
  const resumeRef = useRef(resumeWithSecondFactor);
  resumeRef.current = resumeWithSecondFactor;
  const completeRef = useRef(completeSignIn);
  completeRef.current = completeSignIn;

  useEffect(() => {
    let cancelled = false;
    // Each prompt needs its own server-issued nonce: the backend spends it on
    // redemption, which is what stops a captured token being replayed.
    let nonce = '';

    const prepare = async () => {
      const gis = await loadGoogleIdentity();
      const issued = await googleSignupNonce();
      if (cancelled || !hostRef.current) return;
      nonce = issued.nonce;
      gis.initialize({
        client_id: clientId,
        nonce,
        auto_select: false,
        callback: (response) => void onCredential(response),
      });
      hostRef.current.replaceChildren();
      gis.renderButton(hostRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'center',
        width: Math.min(400, hostRef.current.clientWidth || 400),
      });
    };

    const onCredential = async (response: GisCredentialResponse) => {
      const idToken = response.credential;
      if (!idToken) return;
      if (!termsRef.current) {
        // Refused before anything is sent, so the nonce is still good.
        setError(NEED_TERMS);
        return;
      }
      setError(null);
      setBusy(true);
      const usedNonce = nonce;
      try {
        await googleSignup({ id_token: idToken, accepted_terms: true });
        const { signInWithGoogleIdToken } = await import('../gcp/gcpAuth');
        const firebaseToken = await signInWithGoogleIdToken(idToken, usedNonce);
        // The same continuation as every other sign-in. The router's gates
        // take it from there, including the second-factor enrolment gate:
        // nothing about this path skips it.
        await completeRef.current(firebaseToken);
      } catch (err) {
        // An existing account with a second factor: the token was good and
        // the second-factor screen takes over. Not a failed sign-up, so no
        // error text and nothing in the console.
        if (await resumeRef.current(err)) {
          setBusy(false);
          void prepare().catch(() => undefined);
          return;
        }
        console.warn('Google sign-up failed');
        setError(messageFor(err));
        setBusy(false);
        // The nonce may have been spent. Get a fresh one for the next try.
        void prepare().catch(() => undefined);
      }
    };

    prepare().catch(() => {
      if (!cancelled) setError('Google sign-up is unavailable right now.');
    });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return (
    <div className="basis-0 grow min-w-px flex flex-col items-center gap-2">
      <div
        ref={hostRef}
        className="w-full flex justify-center min-h-[40px]"
        aria-busy={busy}
      />
      {error && (
        <p role="alert" className="text-sm text-red-600 text-center">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A readable message for whatever went wrong after the token was in hand.
 *
 * An existing account held by a different sign-in method is not a failure of
 * this flow: the person is told which kind of sign-in to use. (An
 * account with a second factor never gets here; it is handed to the
 * second-factor screen first.)
 */
function messageFor(err: unknown): string {
  const code = firebaseErrorCode(err) ?? '';
  if (code === 'auth/account-exists-with-different-credential') {
    return SIGNS_IN_ANOTHER_WAY;
  }
  return firebaseAuthMessage(
    err,
    extractErrorMessage(err, 'Google sign-up failed. Please try again.'),
  );
}
